import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { submitChecklistLead } from "@/lib/checklist.functions";

type Locale = "de" | "en";

type Props = {
  locale: Locale;
  checklistSlug: string;
  directDownloadUrl: string;
};

const RATE_LIMIT_KEY = "calv_checklist_requests_v1";
const RATE_LIMIT_MAX = 5;

const T = {
  de: {
    eyebrow: "Lead-Magnet · PDF per E-Mail",
    title: "Checkliste per E-Mail erhalten",
    description:
      "Die Checkliste landet als PDF in Ihrem Postfach. Methodischer Kontakt, kein Newsletter, keine Weitergabe.",
    email: "E-Mail-Adresse",
    consent:
      "Ich stimme der Verarbeitung meiner E-Mail-Adresse für den Versand der Checkliste zu.",
    privacy:
      "Ihre E-Mail wird ausschließlich für den Versand der Checkliste verwendet. Kein Newsletter, keine Weitergabe. Details:",
    privacyLink: "Datenschutz",
    privacyTo: "/datenschutzerklaerung",
    submit: "Checkliste per E-Mail erhalten",
    submitting: "Wird gesendet …",
    successTitle: "Checkliste ist auf dem Weg.",
    successBody: (email: string) =>
      `Die Checkliste wurde an ${email} geschickt. Sehen Sie auch im Spam-Ordner nach, falls die Mail nicht innerhalb weniger Minuten ankommt.`,
    error: "Es ist ein Fehler aufgetreten. Bitte erneut versuchen.",
    rateLimit:
      "Heute sind keine weiteren Anforderungen aus diesem Browser möglich. Bitte morgen erneut versuchen.",
    consentRequired: "Bitte stimmen Sie der Verarbeitung zu, um fortzufahren.",
    placeholderEmail: "ihre.adresse@unternehmen.de",
  },
  en: {
    eyebrow: "Lead magnet · PDF by email",
    title: "Receive the checklist by email",
    description:
      "The checklist lands as a PDF in your inbox. Methodical contact, no newsletter, no sharing.",
    email: "Email address",
    consent:
      "I agree to the processing of my email address for delivery of the checklist.",
    privacy:
      "Your email will be used solely to deliver the checklist. No newsletter, no sharing. Details:",
    privacyLink: "Privacy",
    privacyTo: "/en/privacy-policy",
    submit: "Receive the checklist by email",
    submitting: "Sending …",
    successTitle: "The checklist is on its way.",
    successBody: (email: string) =>
      `The checklist has been sent to ${email}. Please also check your spam folder if the message does not arrive within a few minutes.`,
    error: "Something went wrong. Please try again.",
    rateLimit:
      "No more requests possible from this browser today. Please try again tomorrow.",
    consentRequired: "Please accept the processing terms to continue.",
    placeholderEmail: "your.address@company.com",
  },
} as const;

function readRateState(): { date: string; count: number } {
  if (typeof window === "undefined") return { date: "", count: 0 };
  try {
    const raw = window.localStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return { date: "", count: 0 };
    const parsed = JSON.parse(raw);
    return { date: String(parsed.date ?? ""), count: Number(parsed.count ?? 0) };
  } catch {
    return { date: "", count: 0 };
  }
}

function bumpRateState() {
  if (typeof window === "undefined") return;
  const today = new Date().toISOString().slice(0, 10);
  const cur = readRateState();
  const next =
    cur.date === today ? { date: today, count: cur.count + 1 } : { date: today, count: 1 };
  try {
    window.localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function isRateLimited(): boolean {
  const today = new Date().toISOString().slice(0, 10);
  const cur = readRateState();
  return cur.date === today && cur.count >= RATE_LIMIT_MAX;
}

export function ChecklistDownloadForm({ locale, checklistSlug }: Props) {
  const t = T[locale];
  const submit = useServerFn(submitChecklistLead);
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [consent, setConsent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ email: string } | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!consent) {
      setError(t.consentRequired);
      return;
    }
    if (isRateLimited()) {
      setError(t.rateLimit);
      return;
    }
    setPending(true);
    try {
      await submit({
        data: {
          email,
          name: null,
          company: null,
          role: null,
          checklist_slug: checklistSlug,
          locale,
          marketing_consent: consent,
          source: "email-magnet",
          website,
        },
      });
      bumpRateState();
      setSuccess({ email });
    } catch (err) {
      console.error(err);
      setError(t.error);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="rounded-lg border border-rule bg-white p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">
          {t.eyebrow}
        </p>
        <h3 className="mt-3 font-display text-2xl leading-tight text-ink">{t.title}</h3>
        <p className="mt-4 text-[15px] leading-relaxed text-ink-2">{t.description}</p>

        {success ? (
          <div className="mt-6 rounded-md border border-lime bg-lime/10 p-5">
            <p className="font-display text-lg text-ink">{t.successTitle}</p>
            <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
              {t.successBody(success.email)}
            </p>
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
              <label htmlFor="cl-website">Website</label>
              <input
                id="cl-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <div>
              <label className="font-mono text-[11px] uppercase tracking-wider text-ink-3" htmlFor="cl-email">
                {t.email}
              </label>
              <input
                id="cl-email"
                type="email"
                required
                pattern="^[^@\s]+@[^@\s]+\.[^@\s]+$"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.placeholderEmail}
                className="mt-1 w-full rounded-md border border-rule bg-white px-3 py-2 text-[15px] text-ink outline-none focus:border-cyan"
              />
            </div>
            <label className="flex items-start gap-3 text-[13px] leading-relaxed text-ink-2">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1"
                required
              />
              <span>{t.consent}</span>
            </label>
            <p className="text-[12px] leading-relaxed text-ink-3">
              {t.privacy}{" "}
              <Link to={t.privacyTo} className="underline decoration-cyan/40 underline-offset-2 hover:text-cyan-deep">
                {t.privacyLink}
              </Link>
              .
            </p>
            {error && (
              <p className="rounded-md border border-red-300 bg-red-50 p-3 text-[13px] text-red-700">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={pending}
              className="inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-xs uppercase tracking-wider text-ink transition-colors hover:bg-lime/90 disabled:opacity-60"
            >
              {pending ? t.submitting : `${t.submit} →`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
